import { c as _c } from "react/compiler-runtime";
// `<PnlChart>` — SVG line chart of PnL points. Pure server-side path
// computation; no JS runs at render time. The hero visual on portfolio /
// trade OG cards.

import { OG_TOKENS } from '../tokens';
export interface PnlPoint {
  /** Time index — any monotonic numeric (timestamp, slot, sequence). */
  t: number;
  /** PnL value in display units (e.g. dollars). Sign determines color. */
  pnl: number;
}
export interface PnlChartProps {
  points: readonly PnlPoint[];
  width: number;
  height: number;
  /** When `true`, draws in success-green; otherwise danger-red. Defaults to
   *  the sign of the last point's PnL. */
  positive?: boolean;
}
function pathFromPoints(points: readonly PnlPoint[], width: number, height: number, pad: number): {
  line: string;
  fill: string;
} {
  const xs = points.map(p => p.t);
  const ys = points.map(p => p.pnl);
  const tMin = Math.min(...xs);
  const tMax = Math.max(...xs);
  const yMin = Math.min(...ys, 0);
  const yMax = Math.max(...ys, 0);
  const tRange = tMax - tMin || 1;
  const yRange = yMax - yMin || 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const sx = (t: number): number => pad + (t - tMin) / tRange * innerW;
  const sy = (y: number): number => pad + (1 - (y - yMin) / yRange) * innerH;
  const segs = points.map((p, i): string => {
    const op = i === 0 ? 'M' : 'L';
    return `${op} ${sx(p.t).toFixed(1)} ${sy(p.pnl).toFixed(1)}`;
  });
  const line = segs.join(' ');
  const lastX = sx(tMax).toFixed(1);
  const firstX = sx(tMin).toFixed(1);
  const baselineY = (height - pad).toFixed(1);
  const fill = `${line} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;
  return {
    line,
    fill
  };
}
export function PnlChart(props) {
  const $ = _c(21);
  if (props.points.length < 2) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        display: "flex"
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] !== props.height || $[2] !== props.width) {
      t1 = <svg width={props.width} height={props.height} xmlns="http://www.w3.org/2000/svg" style={t0} />;
      $[1] = props.height;
      $[2] = props.width;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  const last = props.points.at(-1);
  const positive = props.positive ?? (last !== undefined && last.pnl >= 0);
  const stroke = positive ? OG_TOKENS.success500 : OG_TOKENS.danger500;
  let t0;
  if ($[4] !== props.height || $[5] !== props.points || $[6] !== props.width) {
    t0 = pathFromPoints(props.points, props.width, props.height, 4);
    $[4] = props.height;
    $[5] = props.points;
    $[6] = props.width;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  const {
    line,
    fill
  } = t0;
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "flex"
    };
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = positive ? "PnL chart trending up" : "PnL chart trending down";
  let t3;
  if ($[9] !== fill || $[10] !== stroke) {
    t3 = <path d={fill} fill={stroke} fillOpacity="0.18" />;
    $[9] = fill;
    $[10] = stroke;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== line || $[13] !== stroke) {
    t4 = <path d={line} stroke={stroke} strokeWidth="3" fill="none" strokeLinecap="round" />;
    $[12] = line;
    $[13] = stroke;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== props.height || $[16] !== props.width || $[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <svg width={props.width} height={props.height} xmlns="http://www.w3.org/2000/svg" style={t1} role="img" aria-label={t2}>{t3}{t4}</svg>;
    $[15] = props.height;
    $[16] = props.width;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}